package com.qrkontaq.ui.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.qrkontaq.ui.landing.LandingLanguage
import com.qrkontaq.ui.landing.landingLanguage
import com.qrkontaq.ui.theme.Brand
import com.qrkontaq.ui.theme.hexa
import com.qrkontaq.utils.Analytics
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.px

private class ConsentStrings(
    val message: String,
    val accept: String,
    val decline: String,
    val privacy: String,
)

private val LandingLanguage.consent: ConsentStrings
    get() = when (this) {
        // One short sentence: the buttons already spell out the choice, and a
        // second explanatory sentence doubled the banner's height on phones.
        LandingLanguage.ES -> ConsentStrings(
            "Usamos cookies para analítica y publicidad.",
            "Aceptar", "Solo necesarias", "Privacidad",
        )
        LandingLanguage.EN -> ConsentStrings(
            "We use cookies for analytics and advertising.",
            "Accept", "Only necessary", "Privacy",
        )
        LandingLanguage.PT -> ConsentStrings(
            "Usamos cookies para análise e publicidade.",
            "Aceitar", "Apenas necessários", "Privacidade",
        )
        LandingLanguage.FR -> ConsentStrings(
            "Nous utilisons des cookies pour l'analyse et la publicité.",
            "Accepter", "Nécessaires uniquement", "Confidentialité",
        )
        LandingLanguage.UK -> ConsentStrings(
            "Ми використовуємо cookies для аналітики та реклами.",
            "Прийняти", "Лише необхідні", "Приватність",
        )
        LandingLanguage.RU -> ConsentStrings(
            "Мы используем cookies для аналитики и рекламы.",
            "Принять", "Только необходимые", "Приватность",
        )
        LandingLanguage.DE -> ConsentStrings(
            "Wir verwenden Cookies für Analyse und Werbung.",
            "Akzeptieren", "Nur notwendige", "Datenschutz",
        )
    }

/**
 * GDPR / Consent Mode v2 banner. Renders only where the gtag head script is
 * active (qrkontaq.com apex/www — see site/build.gradle.kts) and only until a
 * choice is made; the choice persists in localStorage and is replayed into
 * gtag's consent defaults on every subsequent page load.
 */
@Composable
fun ConsentBanner() {
    var visible by remember { mutableStateOf(Analytics.isActive && Analytics.consentChoice == null) }
    if (!visible) return
    val t = landingLanguage.consent

    // Compact two-row card: message, then buttons + privacy link in one row.
    // The old three-row layout stood ~145px tall and covered 16% of a phone
    // viewport — right where the hero CTA sits.
    Column(
        Modifier
            .position(Position.Fixed)
            .bottom(12.px)
            .left(12.px)
            .right(12.px)
            .maxWidth(440.px)
            .zIndex(2000)
            .backgroundColor(Brand.Surface)
            .borderRadius(Brand.RadiusCard.px)
            .border(1.px, LineStyle.Solid, Brand.Divider)
            .boxShadow(0.px, 8.px, 28.px, color = Color.hexa("1F2421", 0.16f))
            .padding(12.px),
        verticalArrangement = Arrangement.spacedBy(8.px),
    ) {
        SpanText(
            t.message,
            Modifier.fontSize(13.px).lineHeight(1.35).color(Brand.Charcoal),
        )
        Row(
            Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(8.px),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Box(
                Modifier
                    .backgroundColor(Brand.Emerald)
                    .borderRadius(Brand.RadiusChip.px)
                    .padding(topBottom = 8.px, leftRight = 16.px)
                    .cursor(Cursor.Pointer)
                    .onClick {
                        Analytics.grantConsent()
                        visible = false
                    },
            ) {
                SpanText(
                    t.accept,
                    Modifier.fontSize(13.px).fontWeight(FontWeight.SemiBold).color(Brand.Surface),
                )
            }
            Box(
                Modifier
                    .borderRadius(Brand.RadiusChip.px)
                    .border(1.px, LineStyle.Solid, Brand.Divider)
                    .padding(topBottom = 8.px, leftRight = 12.px)
                    .cursor(Cursor.Pointer)
                    .onClick {
                        Analytics.denyConsent()
                        visible = false
                    },
            ) {
                SpanText(
                    t.decline,
                    Modifier.fontSize(13.px).fontWeight(FontWeight.SemiBold).color(Brand.Charcoal),
                )
            }
            Box(Modifier.flexGrow(1))
            SpanText(
                t.privacy,
                Modifier
                    .fontSize(11.px)
                    .color(Brand.TextSecondary)
                    .textDecorationLine(TextDecorationLine.Underline)
                    .cursor(Cursor.Pointer)
                    .onClick { window.location.href = "/privacy" },
            )
        }
    }
}
